import { useState } from 'react';
import { Alert, EmptyState, Modal, Pagination } from '@evogard/ui';
import type { ClaimClosure } from '@evogard/contract';
import {
  formatDate,
  formatInstant,
  type ClaimClosureKind,
  type ManagementLine,
} from '@evogard/domain';
import { closurePhaseLabel } from '../closures/closureLabels.js';
import { useClosureList } from '../closures/useClosures.js';
import { LINE_LABEL, endLabel } from './managementLabels.js';

const PER_PAGE = 25;

/** Um fim do mapa aberto: a linha, o desfecho e desde quando conta. */
export interface OpenEnd {
  line: ManagementLine;
  kind: ClaimClosureKind;
  since: string;
}

/**
 * Os que chegaram a um fim do mapa — "Concluído no Reparo", "Negativa" — no
 * período em que o fim conta. É a lista de Encerrados, filtrada pela linha e
 * pelo desfecho; o clique abre a mesa só para consulta, como lá.
 */
export function EndPanel({
  end,
  canOpen,
  onOpen,
  onClose,
}: {
  end: OpenEnd | null;
  canOpen: (c: ClaimClosure) => boolean;
  onOpen: (c: ClaimClosure) => void;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const query = useClosureList(
    end ? { origin: end.line, kind: end.kind, since: end.since } : {},
    page,
    PER_PAGE,
  );
  const result = end ? query.data : undefined;
  return (
    <Modal
      open={end !== null}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={end ? endLabel(end.kind) : ''}
      descricao={
        end ? `${LINE_LABEL[end.line]} · desde ${formatDate(end.since)}` : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
        {query.isLoading && <p className="text-sm text-texto-suave">Carregando…</p>}
        {result && result.items.length === 0 && (
          <EmptyState icon="historico" title="Ninguém chegou aqui no período">
            Nenhum encerrado neste fim desde {end && formatDate(end.since)}.
          </EmptyState>
        )}
        {result && result.items.length > 0 && (
          <>
            <p className="text-xs text-texto-suave">O encerrado mais recente primeiro.</p>
            <ul className="flex flex-col divide-y divide-borda-suave">
              {result.items.map((c) => (
                <li key={`${c.source}-${c.id}`}>
                  <button
                    type="button"
                    disabled={!canOpen(c)}
                    onClick={() => onOpen(c)}
                    className="flex w-full items-start gap-3 px-1 py-2 text-left transition-colors enabled:hover:bg-superficie-suave disabled:cursor-default"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-semibold text-texto">
                        {c.name ?? 'sem nome'}
                      </span>
                      <span className="truncate text-xs text-texto-suave">
                        {c.source === 'periferico' ? `PER #${c.id}` : `#${c.id}`}
                        {c.plate && (
                          <>
                            {' · '}
                            <span className="font-mono tabular-nums">{c.plate}</span>
                          </>
                        )}
                        {' · '}
                        {closurePhaseLabel(c.origin, c.phase)}
                      </span>
                      {c.reason && (
                        <span className="text-xs text-texto-suave">{c.reason}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs text-texto-suave tabular-nums">
                      {formatInstant(c.closedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              label="encerrados"
            />
          </>
        )}
      </div>
    </Modal>
  );
}
